import HeroSection from '../LandingPage/components/HeroSection';
import InstallAddInButton from '../LandingPage/components/InstallAddInButton';
import ComingSoonButton from '../LandingPage/components/ComingSoonButton';

import type { MetaFunction } from '@remix-run/node';
import { generateMeta } from '~/services/meta';
import { prompts } from '~/services';
import { ArrowDownToLine, ArrowRight } from 'lucide-react';
import { Link } from '@remix-run/react';
import EmblaCarousel from '~/components/EmblaCarousel';
import UserReviews from '~/components/LandingPage/components/UserReviews';
import { Pricing } from '~/components/PricingPage';
import { AiToolsList } from '~/components/LandingPage';
import { FaqSection } from '~/components/FaqSection';
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import WordLogo from '~/assets/word.svg';
import OutlookLogo from '~/assets/outlook.svg';
import TeamsLogo from '~/assets/teams.svg';
import ChromeLogo from '~/assets/chrome.svg';
import GmailLogo from '~/assets/gmail.svg';
import TelegramLogo from '~/assets/telegram.svg';
import OneNote from '~/assets/onenote.svg';
import ExcelLogo from '~/assets/excel.svg';
import SheetsLogo from '~/assets/sheets.svg';
import DocsLogo from '~/assets/docs.svg';
import SlidesLogo from '~/assets/slides.svg';
import PowerPointLogo from '~/assets/powerpoint.svg';
import { EmblaCarouselForReviews } from '~/components/EmblaCarousel';
import { Button } from '~/components/ui/button';

const Integrations = ({
    sectionTitle,
    title,
    description,
    imageUrl,
    installationLink,
}) => {
    return (
        <section id='install' className='py-12 bg-white sm:py-16 lg:py-20'>
            <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                <div className='text-center'>
                    <h2 className='text-3xl font-bold leading-tight text-gray-900 sm:text-4xl xl:text-5xl font-pj'>
                        {sectionTitle}
                    </h2>
                    <p className='mt-4 text-base leading-7 text-gray-600 sm:mt-8 font-pj'>
                        Unlock Office 365's Full Potential. Automate Mundane
                        Business Tasks with AI Perfect Assistant.
                    </p>
                </div>

                <div className='flex items-center justify-center mt-10 text-center'>
                    <div className='md:p-8 lg:p-14 max-w-lg'>
                        <img
                            src={imageUrl}
                            alt={title}
                            className='mx-auto w-20'
                        />
                        <h3 className='mt-12 text-xl font-bold text-gray-900 font-pj'>
                            {title}
                        </h3>
                        <p className='mt-5 text-base text-gray-600 font-pj'>
                            {description}
                        </p>
                        <div className='md:pt-2'>
                            {installationLink ? (
                                <InstallAddInButton url={installationLink} />
                            ) : (
                                <ComingSoonButton />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default function Index({ heroSection, installSection, installElement }) {
    return (
        <>
            <HeroSection {...heroSection} />
            {installElement || <Integrations {...installSection} />}
            <AiToolsList />
            <UserReviews />
            <Pricing />
            <FaqSection />
        </>
    );
}

function Feedback({ message, name, role }) {
    return (
        <div className='bg-gray-900 rounded-xl'>
            <svg
                className='absolute w-auto h-10 text-blue-600 -translate-x-5 -translate-y-4'
                viewBox='0 0 44 38'
                fill='currentColor'
                xmlns='http://www.w3.org/2000/svg'>
                <path d='M44 38H27.1765C25.6975 31.574 24.958 25.8894 24.958 20.9463C24.958 6.98212 31.2437 0 43.8151 0V8.1561C37.8992 8.1561 34.9412 11.3691 34.9412 17.7951V22.0585H44V38ZM19.042 38H2.21849C0.739496 31.574 0 25.8894 0 20.9463C0 6.98212 6.28571 0 18.8571 0V8.1561C12.9412 8.1561 9.98319 11.3691 9.98319 17.7951V22.0585H19.042V38Z' />
            </svg>

            <div className='px-4 py-8 sm:p-6'>
                <div className='mb-2 flex items-center justify-center lg:justify-start'>
                    <svg
                        className='w-5 h-5 text-[#FDB241]'
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 20 20'
                        fill='currentColor'>
                        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                    </svg>
                    <svg
                        className='w-5 h-5 text-[#FDB241]'
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 20 20'
                        fill='currentColor'>
                        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                    </svg>
                    <svg
                        className='w-5 h-5 text-[#FDB241]'
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 20 20'
                        fill='currentColor'>
                        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                    </svg>
                    <svg
                        className='w-5 h-5 text-[#FDB241]'
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 20 20'
                        fill='currentColor'>
                        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                    </svg>
                    <svg
                        className='w-5 h-5 text-[#FDB241]'
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 20 20'
                        fill='currentColor'>
                        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
                    </svg>
                </div>
                <blockquote>
                    <p className='text-base font-normal leading-6 text-white'>
                        {message}
                    </p>
                </blockquote>
                <p className='mt-4 text-base font-semibold text-white'>
                    {name}
                </p>
                <p className='mt-1 text-sm font-normal text-gray-400'>{role}</p>
            </div>
        </div>
    );
}

function AiToolCard({ prompt }) {
    return (
        <Link
            className='group block overflow-hidden w-full border border-1 rounded-xl hover:border-blue-600'
            to={`/tools/${prompt.type}/${prompt.id}`}>
            <div className='flex flex-col p-4 gap-4'>
                <div className='w-full gap-2 flex items-center justify-start'>
                    <span className='text-4xl'>{prompt.emoji}</span>
                    <p className='text-ellipsis max-w-64 whitespace-nowrap overflow-hidden mt-2  text-gray-900 sm:mt-3 text-lg md:text-xl font-pj'>
                        {prompt.title}
                    </p>
                </div>
                <p className='text-gray-600 h-20'>{prompt.description}</p>
                <Button variant='outline'>
                    Try Now
                    <ArrowRight />
                </Button>
            </div>
        </Link>
    );
}

function IntegrationCard({ item }) {
    return (
        <Link
            className='bg-white group block overflow-hidden w-full border border-1 rounded-xl hover:border-blue-600'
            to={item.url}>
            <div className='flex flex-col p-4 gap-4'>
                <div className='w-full gap-2 flex flex-col items-center justify-start'>
                    <p className='text-gray-600 text-ellipsis max-w-64 whitespace-nowrap overflow-hidden mt-2 sm:mt-3 text-lg md:text-xl font-pj'>
                        {item.name}
                    </p>
                    <img src={item.imgSrc} className='w-32 h-32' />
                </div>
                <Button variant='outline'>
                    Install Add-In
                    <ArrowRight />
                </Button>
            </div>
        </Link>
    );
}

const relatedAiTools = (samplePrompts) => {
    return samplePrompts
        .map((pId) => prompts.find((p) => p.id === pId))
        .filter((p) => !!p)
        .map((p) => <AiToolCard prompt={p} />);
};

function Hero({
    imgUrl,
    title,
    subTitle,
    features,
    samplePrompts,
    feedback,
    install,
    installEl,
}) {
    
    return (
        <section className='py-4 overflow-hidden bg-white lg:pb-20 xl:pb-48'>
            <div className='px-4 mx-auto sm:px-6 lg:px-8 max-w-7xl'>
                <div className='grid items-center grid-cols-1 lg:grid-cols-2 gap-y-12 gap-x-16'>
                    <div>
                        <div className='w-full flex items-center justify-center lg:justify-start pb-4'>
                            <img
                                src={imgUrl}
                                className='w-48'
                                alt='#1 AI app for Office365'
                            />
                        </div>
                        <h1 className='text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl xl:text-5xl sm:tracking-tight'>
                            {title}
                        </h1>
                        <p className='mt-6 text-lg leading-7 text-gray-700 lg:leading-8 lg:text-xl'>
                            {subTitle}
                        </p>
                        {installEl && (
                            <div className='mt-8 flex flex-col gap-2 items-center justify-center'>
                                <div className='w-full flex flex-col sm:flex-row gap-2 items-center'>
                                    {installEl}
                                </div>
                            </div>
                        )}
                        {!installEl && install && (
                            <div className='mt-8 flex flex-col gap-2 items-center justify-center'>
                                <div className='w-full flex flex-col sm:flex-row gap-2 items-center'>
                                    {install.map((btn, idx) => (
                                        <Link
                                            target='_blank'
                                            to={btn.url}
                                            title={btn.label}
                                            className='w-full inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white transition-all duration-200 bg-blue-600 border border-transparent rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-700'
                                            role='button'>
                                            {btn.label}
                                        </Link>
                                    ))}
                                </div>
                                {install.length > 1 && (
                                    <span className='text-center text-gray-600'>
                                        ~ or ~
                                    </span>
                                )}
                                {install.length > 1 && (
                                    <a
                                        href='/downloads/AiPerfectAssistant.zip'
                                        title='Download (.EXE) installer for Windows'
                                        download
                                        className='inline-flex items-center pb-2 text-base leading-7 text-gray-600 transition-all duration-200  hover:text-gray-900 font-pj focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-2 '>
                                        Download (.EXE) installer for Windows
                                        <ArrowDownToLine className='ml-1 h-4' />
                                    </a>
                                )}
                            </div>
                        )}

                        <ul className='mt-10 space-y-4'>
                            {features.map((f, idx) => (
                                <li
                                    key={`feature-${idx}`}
                                    className='flex items-center'>
                                    <svg
                                        className='w-6 h-6 mr-2 text-blue-600'
                                        xmlns='http://www.w3.org/2000/svg'
                                        fill='none'
                                        viewBox='0 0 24 24'
                                        stroke='currentColor'
                                        stroke-width='2'>
                                        <path
                                            stroke-linecap='round'
                                            stroke-linejoin='round'
                                            d='M5 13l4 4L19 7'
                                        />
                                    </svg>
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className='relative w-full max-w-sm mx-auto'>
                            <div className='hidden absolute inset-y-0 w-screen translate-y-20 left-1/2 bg-blue-50 lg:left-32 lg:right-0'></div>
                            <div className='flex flex-col gap-8'>
                                <EmblaCarousel
                                    slides={relatedAiTools(samplePrompts)}
                                />
                                <Feedback {...feedback} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const instruction = {
    label: 'ChatGPT Add-In for Microsoft Word',
    videoId: '0a8a672363dd4d47864553ce010f9773',
    paddingTop: '72.5%',
    title: 'tetst',
    steps: [
        <>
            <b>Open Microsoft Word:</b> Highlight any part of the text in
            Microsoft Word document
        </>,
        <>
            <b>Use AI Perfect Assistant:</b> Open AI Perfect Assistant, select
            any AI Prompt Template and Click "Generate with AI"
        </>,
        <>
            <b>Insert result to the document:</b> Click "Insert" to insert AI
            generated text to the Word document
        </>,
    ],
};

const Demonstration = ({ videoDefinition }) => {
    return (
        <div
            style={{
                position: 'relative',
                paddingTop: videoDefinition.paddingTop,
            }}>
            <iframe
                title={`${videoDefinition.label} - AI Perfect Assistant`}
                src={`https://customer-b5iz2r6cpeptxyuk.cloudflarestream.com/${videoDefinition.videoId}/iframe?muted=true&preload=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-b5iz2r6cpeptxyuk.cloudflarestream.com%2F${videoDefinition.videoId}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=true`}
                loading='lazy'
                style={{
                    border: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                }}
                className='rounded-2xl'
                allow='accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;'
                allowFullScreen
            />
        </div>
    );
};

function HowToInstallSection({ instruction }) {
    return (
        <section className='py-4 bg-gray-50 '>
            <div className='px-4 pt-8 mx-auto sm:px-6 lg:px-8 max-w-7xl'>
                <div className='grid items-center grid-cols-1 lg:grid-cols-2 gap-y-12 lg:gap-x-20 xl:gap-x-40'>
                    <div className='rounded-lg'>
                        {instruction.demoEl}
                        {/*<Demonstration videoDefinition={instruction} />*/}
                    </div>
                    <div>
                        <h2 className='text-center text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl lg:text-4xl'>
                            {instruction.title}
                        </h2>
                        <div className='flow-root mt-12 xl:mt-20'>
                            <ul className='-my-8 divide-y divide-gray-200 xl:-my-10'>
                                {instruction.steps.map((st, idx) => (
                                    <li
                                        key={`step-${idx}`}
                                        className='flex flex-col py-8 xl:py-10 sm:flex-row sm:items-start'>
                                        <span className='text-4xl font-medium text-gray-300'>
                                            {' '}
                                            0{idx + 1}.{' '}
                                        </span>
                                        <p className='mt-6 text-xl font-normal text-gray-900 sm:mt-0 sm:ml-9'>
                                            {instruction.steps[idx]}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function GreeenCheckBox() {
    return (
        <svg
            className='w-5 h-5 mx-auto'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'>
            <path
                fill-rule='evenodd'
                d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                clip-rule='evenodd'></path>
        </svg>
    );
}

export function AlternativeToSection({
    title = 'How Does It Compare to Grammarly & QuillBot?',
}) {
    return (
        <section className='py-10 bg-white sm:py-16 lg:py-24'>
            <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                <div className='max-w-xl mx-auto text-center'>
                    <h2 className='text-4xl font-bold text-black lg:text-5xl sm:text-5xl'>
                        {title}
                    </h2>
                    <p className='mt-4 text-lg leading-relaxed text-gray-600'>
                        More Than Grammar. 60+ Tools. Full Office Integration
                    </p>
                </div>

                {/*<!-- lg+ -->*/}
                <div className='hidden mt-16 lg:block'>
                    <table className='w-full'>
                        <thead>
                            <tr>
                                <th className='py-8 pr-4'></th>

                                <th className='px-4 py-8 text-center'>
                                    <span className='text-base font-medium text-blue-600'>
                                        {' '}
                                        Grammarly{' '}
                                    </span>
                                </th>

                                <th className='px-4 py-8 text-center'>
                                    <span className='text-base font-medium text-blue-600'>
                                        {' '}
                                        QuillBot{' '}
                                    </span>
                                </th>

                                <th className='px-4 py-8 text-center bg-gray-900 rounded-t-xl'>
                                    <span className='px-4 py-2 text-base font-medium text-white bg-blue-600 rounded-full'>
                                        {' '}
                                        AI Perfect Assistant{' '}
                                    </span>
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Works natively inside <br />
                                    Word, Outlook, Excel, PowerPoint
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    Only Word
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Supports 60+ AI tools
                                    <br />
                                    (not just grammar/paraphrasing)
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Cheaper than
                                    <br />
                                    Grammarly Business or QuillBot Premium
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Generate reports, summaries, meeting notes
                                    in one click
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-4 pr-4 font-medium border-b border-gray-200'>
                                    Draft professional replies based on selected
                                    input
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center border-b border-gray-200'>
                                    -
                                </td>

                                <td className='px-4 py-4 text-center text-white bg-gray-900 border-b border-white/20'>
                                    <GreeenCheckBox />
                                </td>
                            </tr>

                            <tr>
                                <td className='py-6 pr-4'></td>

                                <td className='px-4 py-6 text-center'></td>

                                <td className='px-4 py-6 text-center'></td>

                                <td className='px-4 py-6 text-center text-white bg-yellow-500 rounded-b-xl'>
                                    <Link
                                        to='/'
                                        title='Explore Integrations'
                                        className='inline-flex items-center font-semibold text-white'>
                                        Explore Integrations{' '}
                                        <ArrowRight className='ml-1' />
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/*<!-- xs to lg -->*/}
            <div className='block mt-12 border-t border-b border-gray-200 divide-y divide-gray-200 lg:hidden'>
                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>
                        <span className='text-sm font-medium text-blue-600'>
                            Grammarly
                        </span>
                    </div>

                    <div className='px-2 py-2'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            QuillBot{' '}
                        </span>
                    </div>

                    <div className='px-2 py-2'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            AI Perfect Assistant{' '}
                        </span>
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Works natively inside <br />
                        Word, Outlook, Excel, PowerPoint
                    </p>
                </div>

                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>Only Word</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Supports 60+ AI tools
                        <br />
                        (not just grammar/paraphrasing)
                    </p>
                </div>

                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Cheaper than
                        <br />
                        Grammarly Business or QuillBot Premium
                    </p>
                </div>

                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Generate reports, summaries, meeting notes in one click
                    </p>
                </div>

                <div className='grid grid-cols-3  text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='px-2 py-4 sm:px-4'>
                    <p className='font-semibold'>
                        Draft professional replies based on selected input
                    </p>
                </div>

                <div className='grid grid-cols-3 text-center divide-x divide-gray-200'>
                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>-</div>

                    <div className='px-2 py-2'>
                        <GreeenCheckBox />
                    </div>
                </div>

                <div className='grid grid-cols-3 text-center divide-x divide-gray-200'>
                    <div className='px-1 py-2 sm:px-4'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            Grammarly{' '}
                        </span>
                    </div>

                    <div className='px-1 py-2 sm:px-4'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            QuillBot{' '}
                        </span>
                    </div>

                    <div className='px-1 py-2 sm:px-4'>
                        <span className='text-sm font-medium text-blue-600'>
                            {' '}
                            AI Perfect Assistant{' '}
                        </span>

                        <Link
                            to='/'
                            title='Explore Integrations'
                            className='flex items-center justify-center w-full px-1 py-2 mt-5 text-sm text-white transition-all duration-200 bg-blue-600 border border-transparent rounded-md hover:bg-blue-700'>
                            Explore Integrations <ArrowRight className='ml-1' />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

function CycleText({ title = 'Also works in' }) {
    const words = [
        'Word',
        'Excel',
        'Outlook',
        'PowerPoint',
        'Teams',
        'Telegram',
        'Chrome',
        'Gmail',
        'Google Slides',
        'Google Docs',
        'Google Sheets',
    ];
    const [index, setIndex] = useState(0);

    const total = words.length;
    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((current) => (current + 1) % total);
        }, 1300);
        return () => clearInterval(interval);
    }, [total]);

    return (
        <span className='text-4xl font-bold text-black lg:text-5xl sm:text-5xl'>
            {title}&nbsp;
            <br />
            <AnimatePresence mode='wait'>
                <motion.h1
                    key={`words_${index}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.08 }}
                    className='inline-block text-blue-700'>
                    &quot;{words[index]}&quot;
                </motion.h1>
            </AnimatePresence>
        </span>
    );
}

export const integrationsList = [
    { name: 'Word', url: '/chatgpt-for-microsoft-word', imgSrc: WordLogo },
    { name: 'Excel', url: '/chatgpt-for-microsoft-excel', imgSrc: ExcelLogo },
    {
        name: 'PowerPoint',
        url: '/chatgpt-for-microsoft-powerpoint',
        imgSrc: PowerPointLogo,
    },
    { name: 'Teams', url: '/chatgpt-for-microsoft-teams', imgSrc: TeamsLogo },
    {
        name: 'Outlook',
        url: '/chatgpt-for-microsoft-outlook',
        imgSrc: OutlookLogo,
    },
    {
        name: 'OneNote',
        url: '/chatgpt-for-onenote',
        imgSrc: OneNote,
    },
    { name: 'Gmail', url: '/chatgpt-for-gmail', imgSrc: GmailLogo },
    /*
    { name: 'Google Docs', url: '/chatgpt-for-google-docs', imgSrc: DocsLogo },
    {
        name: 'Google Sheets',
        url: '/chatgpt-for-google-sheets',
        imgSrc: SheetsLogo,
    },
    {
        name: 'Google Slides',
        url: '/chatgpt-for-google-slides',
        imgSrc: SlidesLogo,
    },
    */
    { name: 'Telegram', url: '/chatgpt-for-telegram', imgSrc: TelegramLogo },
    { name: 'Chrome', url: '/chatgpt-for-chrome', imgSrc: ChromeLogo },
];

export function IntegrationsHeader({ title }) {
    return (
        <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
            <div className='max-w-2xl mx-auto text-center'>
                <CycleText title={title} />
                <p className='mt-4 text-lg leading-relaxed text-gray-600'>
                    Stay in Word, Outlook, Excel, or PowerPoint — and work
                    smarter with AI in one click.
                </p>
            </div>
        </div>
    );
}

export function IntegrationsList({ title }) {
    return (
        <section className='py-12 bg-gray-50 sm:py-16 lg:py-20'>
            <IntegrationsHeader title={title} />

            <div className='mt-9 lg:mt-12 mx-4'>
                <EmblaCarouselForReviews
                    slides={integrationsList.map((item, idx) => (
                        <IntegrationCard
                            key={`integration-${idx}`}
                            item={item}
                        />
                    ))}
                />
            </div>
            <div className='mt-2 text-center md:mt-4'>
                <a
                    href='/integrations'
                    title='Browse All Integrations'
                    className='inline-flex items-center pb-2 text-base leading-7 text-gray-600 transition-all duration-200  hover:text-gray-900 font-pj focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-2 '>
                    Browse All Integrations <ArrowRight className='ml-1 h-4' />
                </a>
            </div>
        </section>
    );
}

export const MicroLandingNew = ({ hero = {}, instruction = {} }) => (
    <>
        <Hero {...hero} />
        <HowToInstallSection instruction={instruction} />
        <IntegrationsList />
        <UserReviews />
        <AlternativeToSection />
        <AiToolsList />
    </>
);
